import { get, size, toLower, pick, startsWith } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getAssetsExposureFieldName, getAssetsFactorSensitivityFieldName } from "helpers/assets"
import { generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getIsActive, getSelectedMetricType, getSelectedPage, getIdSort, getGroupingOptions, getMenuItems, getSelectedSubView, getMetricTypeOptions, getSubViewOptions, getModelId, getAttributionMethod, getColumnOrderOptions } from "./selectors"
import { getAggregatedData, getScaledColumnsByCategories, getCompositionStatus, getEndDate, getSecurities, getQueries, getOrderedSecuritiesWithoutCashOffset } from "./selectors/data"
import { getDownloadData, getDownloadButtonTooltip } from "./selectors/download"
import { getGroupDropdown } from "./selectors/group"
import { getMissingDataFiles, betasAreMissing, betasQueryFragment } from "selectors/missingDataFiles"
import { VIEW_TYPES } from "helpers/base"
import { getSelectedViewIsContentSet } from "selectors/contentSets"
import { getIsOpPro } from "modules/account-root/selectors"
import { getPortfolioBetaInstrument } from "modules/portfolio-root/selectors/beta-instrument"
import { getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { FACTOR_SENSITIVITY_TYPES, getFactorSensitivityQueryParams } from "helpers/factorSensitivity"
import { getSelectedView } from "selectors/assets"
import withUrlParams from "hocs/withUrlParams"
import { MODEL_METADATA_QUERY } from "resources/models"
import { getIsRfrAttributionEnabled } from "selectors/features"
import { getResidualReturnSubquery } from "helpers/residualAttribution"

export default withUrlParams(createModule({
  view,
  externalProps: (state, props) => ({
    classifications: getClassifications(state),
    metricType: getSelectedMetricType(state, props),
    endDate: getEndDate(state, props),
    groupingOptions: getGroupingOptions(state, props),
    modelId: getModelId(state, props),
    page: getSelectedPage(state),
    queries: getQueries(state, props),
    selectedView: getSelectedView(state, props),
    selectedSubView: getSelectedSubView(state, props),
    missingDataFiles: getMissingDataFiles(state, props),
    includeNotHeld: get(props, "viewType") === VIEW_TYPES.ACTIVE,
    selectedViewIsContentSet: getSelectedViewIsContentSet(state, props),
    betaInstrument: getPortfolioBetaInstrument(state, props),
    currency: getCurrency(state),
    isActive: getIsActive(state, props),
    attributionMethod: getAttributionMethod(state, props),
    accountId: get(state, "global.selected.accountId"),
    isRfrAttributionEnabled: getIsRfrAttributionEnabled(state),
  }),
  required: ["account", "isOpPro", "modelId", "assetId", "assetType"],
  data: [{
    resource: "selectedContentSetCategories",
    as: null,
    url: ({ selectedView, selectedViewIsContentSet }) => {
      if (!selectedView || !selectedViewIsContentSet) {
        return null
      }
      const query = `query ($contentSetId: String!) {
        contentSet(id: $contentSetId){
          categories{
            id,
            name,
            factors{
              id
              name
            }
          }
        }
      }`

      return {
        id: `selectedContentSetCategories-${selectedView}`,
        query,
        variables: {
          contentSetId: selectedView,
        },
      }
    },
  }, {
    resource: "assetAnalyticsComposition",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, segment, classifications, includeNotHeld, queries, groupingOptions, expand }) => {
      if (!queries.composition || groupingOptions) {
        return null
      }

      return {
        id: `assetAnalyticsComposition-${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $on: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $segment: Segment, $includeNotHeld: Boolean, $expand: Boolean) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              composition(includeNotHeld: $includeNotHeld, scaleFormat: PERCENT_EQUITY_GMV) {
                positionsCount
                gmv
                referenceEquity
                positions {
                  id
                  assetClass
                  assetSubclass
                  description
                  isUnmodeled
                  modelProviderId
                  country
                  currency
                  economicExposure
                  basePercentEquity: baseEconomicExposure
                  positionPercentEquity: positionSetEconomicExposure
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  bloombergTicker
                  notHeld
                }
              }
            }
            compositionDollar: simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              composition(includeNotHeld: $includeNotHeld) {
                positions {
                  id
                  assetClass
                  modelProviderId
                  economicExposure: positionSetEconomicExposure
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedComposition",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, segment, classifications, queries, includeNotHeld, expand }) => {
      if (!queries.composition || !groupingOptions) {
        return null
      }

      const query = `query ($modelId: String!, $on: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $includeNotHeld: Boolean, $expand: Boolean) {
        model (id: $modelId) {
          simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
            composition (scaleFormat: PERCENT_EQUITY_GMV, includeNotHeld: $includeNotHeld) {
              gmv
              referenceEquity
              compositionBy(groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                id
                name: description
                economicExposure
                basePercentEquity: baseEconomicExposure
                positionPercentEquity: positionSetEconomicExposure
                contributors {
                  id
                  assetClass
                  country
                  currency
                  description
                  isUnmodeled
                  modelProviderId
                  economicExposure
                  basePercentEquity: baseEconomicExposure
                  positionPercentEquity: positionSetEconomicExposure
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  bloombergTicker
                  notHeld
                }
              }
            }
          }
          compositionDollar: simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
            composition(includeNotHeld: $includeNotHeld) {
              compositionBy(groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                id
                economicExposure: positionSetEconomicExposure
                contributors {
                  id
                  assetClass
                  modelProviderId
                  economicExposure: positionSetEconomicExposure
                }
              }
            }
          }
        }
      }`

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}`,
        query,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...groupingOptions,
        },
      }
    },
  }, {
    resource: "assetAnalyticsPerformance",
    url: ({ modelId, endDate, performanceStartDate, assetId, assetType, viewType, baseSignature, base, segment, classifications, includeNotHeld, queries, groupingOptions, expand, currency, isRfrAttributionEnabled }) => {
      if ((!queries.performance && !queries.performanceFactors) || groupingOptions) {
        return null
      }

      return {
        id: `${modelId}${endDate}${performanceStartDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${isRfrAttributionEnabled}`,
        query: `query ($modelId: String!, $from: Date!, $to: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $includeNotHeld: Boolean, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $from, to: $to) {
              performanceContributors(includeNotHeld: $includeNotHeld) {
                id
                assetClass
                notHeld
                description
                isUnmodeled
                country
                currency
                modelProviderId
                ${generateClassificationQueryForContributors("GICS", classifications)}
                bloombergTicker
                totalReturn {
                  cumulativePercent
                  cumulativeCurrency
                }
                attribution {
                  summary {
                    factorsReturn {
                      cumulativePercent
                      cumulativeCurrency
                    }
                    specificReturn {
                      cumulativePercent
                      cumulativeCurrency
                    }
                    tradingReturn {
                      cumulativePercent
                      cumulativeCurrency
                    }
                    ${isRfrAttributionEnabled ? getResidualReturnSubquery(true) : ""}
                  }
                  factors {
                    id
                    name
                    category
                    return {
                      cumulativePercent
                      cumulativeCurrency
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          from: performanceStartDate,
          to: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedPerformance",
    url: ({ modelId, endDate, performanceStartDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, segment, classifications, includeNotHeld, queries, expand, currency, isRfrAttributionEnabled }) => {
      if ((!queries.performance && !queries.performanceFactors) || !groupingOptions) {
        return null
      }

      const residualReturnSubquery = isRfrAttributionEnabled ? getResidualReturnSubquery(true) : ""

      return {
        id: `${modelId}${endDate}${performanceStartDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}${currency}${isRfrAttributionEnabled}`,
        query: `query ($modelId: String!, $from: Date!, $to: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $includeNotHeld: Boolean, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $from, to: $to) {
              groupedPerformanceContributors(includeNotHeld: $includeNotHeld, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                name
                id
                totalReturn {
                  cumulativePercent
                  cumulativeCurrency
                }
                attribution {
                  summary {
                    factorsReturn {
                      cumulativePercent
                      cumulativeCurrency
                    }
                    specificReturn {
                      cumulativePercent
                      cumulativeCurrency
                    }
                    tradingReturn {
                      cumulativePercent
                      cumulativeCurrency
                    }
                    ${residualReturnSubquery}
                  }
                  factors {
                    id
                    name
                    category
                    return {
                      cumulativePercent
                      cumulativeCurrency
                    }
                  }
                }
                contributors {
                  id
                  assetClass
                  totalReturn {
                    cumulativePercent
                    cumulativeCurrency
                  }
                  notHeld
                  description
                  isUnmodeled
                  country
                  currency
                  modelProviderId
                  bloombergTicker
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  attribution {
                    summary{
                      factorsReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      specificReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      tradingReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      ${residualReturnSubquery}
                    }
                    factors {
                      id
                      name
                      category
                      return {
                        cumulativePercent
                        cumulativeCurrency
                      }
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          from: performanceStartDate,
          to: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
          ...groupingOptions,
        },
      }
    },
  }, {
    resource: "assetAnalyticsRisk",
    url: ({ modelId, endDate, assetId, assetType, isOpPro, isActive, baseSignature, base, segment, includeNotHeld, queries, expand, currency, attributionMethod }) => {
      if ((!queries.risk && !queries.marginal) || isOpPro) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${isActive}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}`,
        query: `query (
            $modelId: String!
            $on: Date!
            $assetId: String!
            $assetType: PositionSetType!
            $base: [PositionSetInput]
            $includeNotHeld: Boolean
            $segment: Segment
            $expand: Boolean
            $attributionMethod: RiskContributorAttributionMethod
          ) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              risk {
                total
              }
              riskContributors(includeNotHeld: $includeNotHeld, attributionMethod: $attributionMethod) {
                id
                description
                modelProviderId
                assetClass
                notHeld
                total
                attribution{
                  summary{
                    factors
                    specific
                  }

                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: isActive ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
          attributionMethod,
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedRisk",
    url: ({ modelId, endDate, assetId, assetType, isOpPro, isActive, baseSignature, base, groupingOptions, segment, includeNotHeld, queries, expand, currency, attributionMethod }) => {
      if ((!queries.risk && !queries.marginal) || !groupingOptions || isOpPro) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${isActive}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}`,
        query: `query (
            $modelId: String!
            $on: Date!
            $assetId: String!
            $assetType: PositionSetType!
            $base: [PositionSetInput]
            $includeNotHeld: Boolean
            $groupBy: ContributorGroupType!
            $groupById: String
            $tier: String
            $segment: Segment
            $expand: Boolean
            $attributionMethod: RiskContributorAttributionMethod
          ) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              groupedRiskContributors(includeNotHeld: $includeNotHeld, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier attributionMethod: $attributionMethod) {
                id
                name
                attribution {
                  summary {
                    factors
                    specific
                  }
                }
                contributors {
                  id
                  description
                  modelProviderId
                  assetClass
                  notHeld
                  total
                  attribution {
                    summary {
                      factors
                      specific
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: isActive ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
          attributionMethod,
          ...groupingOptions,
        },
      }
    },
  }, {
    resource: "assetAnalyticsBeta",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, segment, includeNotHeld, queries, groupingOptions, expand, missingDataFiles }) => {
      // missingDataFiles contains the ids of missing data per modelId
      // eg. { QES-US: ['predictedBeta'], ... }
      // because we are checking for existence of a fileId in the object, we need to wait for the missingDataFiles to actually be loaded to prevent a false negative
      if (!queries.beta || groupingOptions || !missingDataFiles || betasAreMissing(missingDataFiles, modelId)) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $on: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $includeNotHeld: Boolean, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              betaContributors(includeNotHeld: $includeNotHeld){
                contributors {
                  id
                  modelProviderId
                  assetClass
                  notHeld
                  ${betasQueryFragment(missingDataFiles)}
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          expand,
          segment,
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedBeta",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, segment, includeNotHeld, queries, expand, missingDataFiles }) => {
      // missingDataFiles contains the ids of missing data per modelId
      // eg. { QES-US: ['predictedBeta'], ... }
      // because we are checking for existence of a fileId in the object, we need to wait for the missingDataFiles to actually be loaded to prevent a false negative
      if (!queries.beta || !groupingOptions || !missingDataFiles || betasAreMissing(missingDataFiles)) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $on: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $includeNotHeld: Boolean, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              groupedBetaContributors(includeNotHeld: $includeNotHeld,  groupBy: $groupBy, groupById: $groupById, classificationTier: $tier){
                groupedContributors {
                  name
                  id
                  ${betasQueryFragment(missingDataFiles, true)}
                  contributors {
                    id
                    modelProviderId
                    assetClass
                    notHeld
                    ${betasQueryFragment(missingDataFiles)}
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
          ...groupingOptions,
        },
      }
    },
  }, {
    resource: "assetAnalyticsDynamicPredictedBeta",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, segment, includeNotHeld, queries, expand, betaInstrument }) => {
      if (!queries.dynamicBeta || groupingOptions || !betaInstrument) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${JSON.stringify(betaInstrument)}`,
        query: `query ($modelId: String!, $on: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $includeNotHeld: Boolean, $segment: Segment, $expand: Boolean, $betaInstrument: PositionSetInput) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              betaContributors(includeNotHeld: $includeNotHeld, instrument: $betaInstrument){
                contributors {
                  id
                  modelProviderId
                  assetClass
                  notHeld
                  dynamicPredicted: predicted
                  securityBeta{
                    dynamicPredicted: predicted
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          expand,
          segment,
          betaInstrument: pick(betaInstrument, ["id", "type"]),
        },
      }
    },
  }, {
    resource: "assetAnalyticsDynamicPredictedGroupedBeta",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, segment, includeNotHeld, queries, expand, betaInstrument }) => {
      if (!queries.dynamicBeta || !groupingOptions || !betaInstrument) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}${JSON.stringify(betaInstrument)}`,
        query: `query ($modelId: String!, $on: Date!, $assetId: String!, $assetType: PositionSetType!, $base: [PositionSetInput], $includeNotHeld: Boolean, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean, $betaInstrument: PositionSetInput) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              groupedBetaContributors(includeNotHeld: $includeNotHeld,  groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, instrument: $betaInstrument){
                groupedContributors {
                  name
                  id
                  dynamicPredicted: predicted
                  contributors {
                    id
                    modelProviderId
                    assetClass
                    notHeld
                    dynamicPredicted: predicted
                    securityBeta{
                      dynamicPredicted: predicted
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
          betaInstrument: pick(betaInstrument, ["id", "type"]),
          ...groupingOptions,
        },
      }
    },
  }, {
    resource: "assetAnalyticsExposure",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, queries, selectedView, selectedSubView, includeNotHeld, segment, metricType, expand, selectedViewIsContentSet }) => {
      if ((!queries.exposure && !selectedViewIsContentSet) || groupingOptions || (selectedViewIsContentSet && !selectedSubView)) {
        return null
      }

      const contentSetQueryVariable = !selectedViewIsContentSet ? { declaration: "", usage: "" } : {
        declaration: ", $contentSetId: String!",
        usage: ", contentSetId: $contentSetId",
      }

      const query = `query ($modelId: String!, $assetId: String!, $assetType: PositionSetType!, $date: Date!, $base: [PositionSetInput], $selectedSubView: [String]!, $includeNotHeld: Boolean, $segment: Segment, $expand: Boolean${contentSetQueryVariable.declaration}) {
        model(id: $modelId) {
          simulation(
            positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}
            from: $date
            to: $date
            base: $base
            interval: AUTO
          ) {
            exposureContributors(includeNotHeld: $includeNotHeld${contentSetQueryVariable.usage}) {
              contributors {
                id
                assetClass
                modelProviderId
                factors(category: $selectedSubView) {
                  id
                  ${getAssetsExposureFieldName(metricType)}
                }
              }
            }
          }
        }
      }`
      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${selectedView}${selectedSubView}${metricType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}`,
        query,
        variables: {
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          selectedSubView: toLower(selectedSubView),
          includeNotHeld,
          modelId,
          date: endDate,
          assetId,
          assetType,
          segment,
          expand,
          ...(selectedViewIsContentSet && { contentSetId: selectedView }),
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedExposure",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, queries, selectedView, selectedSubView, includeNotHeld, segment, metricType, expand, selectedViewIsContentSet }) => {
      if (!queries.exposure || !groupingOptions || (selectedViewIsContentSet && !selectedSubView)) {
        return null
      }

      const contentSetQueryVariable = !selectedViewIsContentSet ? { declaration: "", usage: "" } : {
        declaration: ", $contentSetId: String!",
        usage: ", contentSetId: $contentSetId",
      }

      const query = `query ($modelId: String!, $assetId: String!, $assetType: PositionSetType!, $date: Date!, $base: [PositionSetInput], $selectedSubView: [String]!, $includeNotHeld: Boolean, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean${contentSetQueryVariable.declaration}) {
        model(id: $modelId) {
          simulation(
            positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}
            from: $date
            to: $date
            base: $base
            interval: AUTO
          ) {
            groupedExposureContributors(includeNotHeld: $includeNotHeld, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier${contentSetQueryVariable.usage})  {
              groupedContributors {
                id
                name
                factors(category: $selectedSubView) {
                  id
                  ${getAssetsExposureFieldName(metricType, true)}
                }
                contributors {
                  id
                  assetClass
                  modelProviderId
                  factors(category: $selectedSubView) {
                    id
                    ${getAssetsExposureFieldName(metricType)}
                  }
                }
              }
            }
          }
        }
      }`

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${selectedView}${selectedSubView}${metricType}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}`,
        query,
        variables: {
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          selectedSubView,
          includeNotHeld,
          modelId,
          date: endDate,
          assetId,
          assetType,
          segment,
          expand,
          ...groupingOptions,
          ...(selectedViewIsContentSet && { contentSetId: selectedView }),
        },
      }
    },
  }, {
    resource: "assetAnalyticsRiskFactors",
    url: ({ modelId, endDate, assetId, assetType, isActive, baseSignature, base, groupingOptions, queries, includeNotHeld, segment, metricType, expand, currency, attributionMethod }) => {
      if (!queries.riskFactors || groupingOptions) {
        return null
      }
      const query = `query (
          $modelId: String!
          $assetId: String!
          $assetType: PositionSetType!
          $date: Date!
          $base: [PositionSetInput]
          $includeNotHeld: Boolean
          $segment: Segment
          $expand: Boolean
          $attributionMethod: RiskContributorAttributionMethod
        ) {
        model(id: $modelId) {
          simulation(
            positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}
            from: $date
            to: $date
            base: $base
          ) {
            riskContributors(on: $date, includeNotHeld: $includeNotHeld attributionMethod: $attributionMethod) {
              id
              assetClass
              modelProviderId
              attribution {
                factors {
                  id
                  name
                  category
                  value
                }
              }
            }
          }
        }
      }`

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${isActive}${metricType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}`,
        query,
        variables: {
          base: isActive ? [base] : undefined,
          includeNotHeld,
          modelId,
          date: endDate,
          assetId,
          assetType,
          segment,
          expand,
          attributionMethod,
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedRiskFactors",
    url: ({ modelId, endDate, assetId, assetType, isActive, baseSignature, base, groupingOptions, queries, includeNotHeld, segment, metricType, expand, currency, attributionMethod }) => {
      if (!queries.riskFactors || !groupingOptions) {
        return null
      }
      const query = `query (
          $modelId: String!
          $assetId: String!
          $assetType: PositionSetType!
          $date: Date!
          $base: [PositionSetInput]
          $includeNotHeld: Boolean
          $groupBy: ContributorGroupType!
          $groupById: String
          $tier: String
          $segment: Segment
          $expand: Boolean
          $attributionMethod: RiskContributorAttributionMethod
        ) {
        model(id: $modelId) {
          simulation(
            positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}
            from: $date
            to: $date
            base: $base
          ) {
            groupedRiskContributors(includeNotHeld: $includeNotHeld groupBy: $groupBy groupById: $groupById classificationTier: $tier attributionMethod: $attributionMethod) {
              id
              name
              attribution {
                factors {
                  id
                  name
                  category
                  value
                }
              }
              contributors {
                id
                assetClass
                modelProviderId
                attribution {
                  factors {
                    id
                    name
                    category
                    value
                  }
                }
              }
            }
          }
        }
      }`

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${isActive}${metricType}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}`,
        query,
        variables: {
          base: isActive ? [base] : undefined,
          includeNotHeld,
          modelId,
          date: endDate,
          assetId,
          assetType,
          segment,
          expand,
          attributionMethod,
          ...groupingOptions,
        },
      }
    },
  }, {
    resource: "assetAnalyticsFactorSensitivity",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, queries, includeNotHeld, segment, metricType, expand, currency, selectedView }) => {
      if (!queries.sensitivity || groupingOptions) {
        return null
      }
      const factorSensitivityType = startsWith(selectedView, "predicted") ? FACTOR_SENSITIVITY_TYPES.PREDICTED : FACTOR_SENSITIVITY_TYPES.HISTORICAL

      const query = `query ($modelId: String!, $assetId: String!, $assetType: PositionSetType!, $date: Date!, $base: [PositionSetInput],  $includeNotHeld: Boolean, $segment: Segment, $expand: Boolean, $factorSensitivityType: FactorSensitivityType!, $days: PositiveInt, $stdDev: PositiveFloat, $horizon: FactorSensitivityTimeHorizon!) {
        model(id: $modelId) {
          simulation(
            positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}
            from: $date
            to: $date
            base: $base
          ) {
            factorSensitivityContributors(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days includeNotHeld: $includeNotHeld) {
              id
              assetClass
              modelProviderId
              factors {
                id
                name
                category
                ${getAssetsFactorSensitivityFieldName(metricType)}
              }
            }
          }
        }
      }`

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${factorSensitivityType}${metricType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}`,
        query,
        variables: {
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          modelId,
          date: endDate,
          assetId,
          assetType,
          segment,
          expand,
          ...getFactorSensitivityQueryParams(factorSensitivityType),
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedFactorSensitivity",
    url: ({ modelId, endDate, assetId, assetType, viewType, baseSignature, base, groupingOptions, queries, selectedView, includeNotHeld, segment, metricType, expand, currency, selectedSubView }) => {
      if (!queries.sensitivity || !groupingOptions) {
        return null
      }
      const factorSensitivityType = startsWith(selectedView, "predicted") ? FACTOR_SENSITIVITY_TYPES.PREDICTED : FACTOR_SENSITIVITY_TYPES.HISTORICAL

      const query = `query ($modelId: String!, $assetId: String!, $assetType: PositionSetType!, $date: Date!, $base: [PositionSetInput], $selectedSubView: [String]!, $includeNotHeld: Boolean, $segment: Segment, $expand: Boolean, $factorSensitivityType: FactorSensitivityType!, $days: PositiveInt, $stdDev: PositiveFloat, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $horizon: FactorSensitivityTimeHorizon!) {
        model(id: $modelId) {
          simulation(
            positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}
            from: $date
            to: $date
            base: $base
          ) {
            groupedFactorSensitivityContributors(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days includeNotHeld: $includeNotHeld groupBy: $groupBy groupById: $groupById classificationTier: $tier) {
              id
              name
              factors(category: $selectedSubView) {
                id
                name
                category
                ${getAssetsFactorSensitivityFieldName(metricType, true)}
              }
              contributors {
                id
                assetClass
                modelProviderId
                factors(category: $selectedSubView) {
                  id
                  name
                  category
                  ${getAssetsFactorSensitivityFieldName(metricType)}
                }
              }
            }
          }
        }
      }`

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${viewType}${factorSensitivityType}${metricType}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${JSON.stringify(groupingOptions)}${selectedSubView}`,
        query,
        variables: {
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          modelId,
          date: endDate,
          assetId,
          assetType,
          selectedSubView,
          segment,
          expand,
          ...groupingOptions,
          ...getFactorSensitivityQueryParams(factorSensitivityType),
        },
      }
    },
  }, {
    resource: "assetAnalyticsMarginalContribution",
    url: ({ modelId, endDate, assetId, assetType, isOpPro, isActive, baseSignature, base, segment, includeNotHeld, queries, expand, currency, attributionMethod }) => {
      if (!queries.marginal || isOpPro) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${isActive}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}`,
        query: `query (
            $modelId: String!
            $on: Date!
            $assetId: String!
            $assetType: PositionSetType!
            $base: [PositionSetInput]
            $includeNotHeld: Boolean
            $segment: Segment
            $expand: Boolean
            $attributionMethod: RiskContributorAttributionMethod
          ) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              risk {
                total
              }
              riskContributors(includeNotHeld: $includeNotHeld attributionMethod: $attributionMethod) {
                id
                modelProviderId
                assetClass
                notHeld
                total
                exposureRiskCorrelation{
                  volatility
                  correlation
                  marginalContribution
                  riskContribution
                }
                attribution {
                  exposureRiskCorrelation{
                    factors{
                      volatility
                      correlation
                      marginalContribution
                      riskContribution
                    }
                    specific{
                      volatility
                      correlation
                      marginalContribution
                      riskContribution
                    }
                  }
                  summary {
                    factors
                    specific
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: isActive ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
          attributionMethod,
        },
      }
    },
  }, {
    resource: "assetAnalyticsGroupedMarginalContribution",
    url: ({ modelId, endDate, assetId, assetType, isOpPro, isActive, baseSignature, base, groupingOptions, segment, includeNotHeld, queries, expand, currency, attributionMethod }) => {
      if (!queries.marginal || !groupingOptions || isOpPro) {
        return null
      }

      return {
        id: `${modelId}${endDate}${assetType}${assetId}${expand}${isActive}${baseSignature}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}${currency}${attributionMethod}`,
        query: `query (
            $modelId: String!
            $on: Date!
            $assetId: String!
            $assetType: PositionSetType!
            $base: [PositionSetInput]
            $includeNotHeld: Boolean
            $groupBy: ContributorGroupType!
            $groupById: String
            $tier: String
            $segment: Segment
            $expand: Boolean
            $attributionMethod: RiskContributorAttributionMethod
          ) {
          model (id: $modelId) {
            simulation (positionSet: {type: $assetType, id: $assetId, segment: $segment, expand: $expand}, base: $base, from: $on, to: $on) {
              groupedRiskContributors(includeNotHeld: $includeNotHeld, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier attributionMethod: $attributionMethod) {
                id
                name
                exposureRiskCorrelation {
                  riskContribution
                }
                attribution {
                  exposureRiskCorrelation {
                    factors {
                      volatility
                      correlation
                      riskContribution
                    }
                    specific {
                      volatility
                      correlation
                      riskContribution
                    }
                  }
                }
                contributors {
                  id
                  modelProviderId
                  assetClass
                  notHeld
                  total
                  exposureRiskCorrelation {
                    volatility
                    correlation
                    marginalContribution
                    riskContribution
                  }
                  attribution {
                    exposureRiskCorrelation {
                      factors {
                        volatility
                        correlation
                        marginalContribution
                        riskContribution
                      }
                      specific {
                        volatility
                        correlation
                        marginalContribution
                        riskContribution
                      }
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: endDate,
          assetId,
          assetType,
          base: isActive ? [base] : undefined,
          includeNotHeld,
          segment,
          expand,
          attributionMethod,
          ...groupingOptions,
        },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile2,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
  mapStateToProps: (state, props) => ({
    aggregatedRow: getAggregatedData(state, props),
    columns: getScaledColumnsByCategories(state, props),
    count: size(getOrderedSecuritiesWithoutCashOffset(state, props)),
    downloadData: getDownloadData(state, props),
    downloadButtonTooltip: getDownloadButtonTooltip(state, props),
    foundSecurities: getSecurities(state, props),
    groupDropdown: getGroupDropdown(state, props),
    idSort: getIdSort(state, props),
    menuItems: getMenuItems(state, props),
    metricTypeOptions: getMetricTypeOptions(state, props),
    page: getSelectedPage(state),
    status: getCompositionStatus(state),
    subViewOptions: getSubViewOptions(state, props),
    columnOrderOptions: getColumnOrderOptions(state, props),
    columnOrder: state.modules.assetAnalyticsTable.columnOrder,
    isOpPro: getIsOpPro(state, props),
  }),
}))
